/**
 * WB4.5 — Domain action manifests for non-sellable runtime.
 *
 * Propagates sellable golden patterns: top-N palette parity · bulk top-1 · risk tier · wired handlers.
 */

import { ROLES } from '@shared/types/roles';
import { BULK_STATE_CHANGE_HANDLER_REFS } from './workbench-bulk-handler-refs';
import {
  COLLECTION_DOMAIN_ACTION_IDS,
  COLLECTION_DOMAIN_HANDLER_REFS,
  SUPPLIER_RECEIVING_DOMAIN_ACTION_IDS,
  SUPPLIER_RECEIVING_DOMAIN_HANDLER_REFS,
} from './workbench-domain-action-handler-refs';
import { COLLECTION_CONTACT_FLOW_OPERATOR_LABELS } from './collection-contact-flow-operator-labels';
import { COLLECTION_COMMITMENT_OPERATOR_LABELS } from './collection-commitment-operator-labels';
import { SUPPLIER_GOODS_RECEIPT_FLOW_OPERATOR_LABELS } from './supplier-goods-receipt-flow-operator-labels';
import { buildActionSafety } from './workbench-action-safety-policy';
import type { ActionRuleEntry, WorkbenchAction } from './action-types';
import type { WorkbenchActionSafety } from './workbench-action-safety-policy';

/** Wired handler refs shared with sellable golden slice — duplicated to avoid import cycle. */
const SHARED_WORKBENCH_HANDLER_REFS = {
  openDetail: 'workbench:open-detail',
  viewAudit: 'workbench:view-audit',
  viewAuditBulk: 'workbench:view-audit-bulk',
  startInventoryWorkflow: 'flow:start:item.inventory',
  startOrder: 'flow:start:order_intake',
} as const;

export type DomainActionFamily =
  | 'entity'
  | 'finance'
  | 'warehouse'
  | 'item'
  | 'inventory'
  | 'manufacturing'
  | 'customer'
  | 'supplier'
  | 'order'
  | 'collection'
  | 'task'
  | 'reference';

export type ActionRiskTier = 'low' | 'medium' | 'high' | 'finance';

export const DOMAIN_ACTION_HANDLER_REFS = {
  openDetail: SHARED_WORKBENCH_HANDLER_REFS.openDetail,
  viewAudit: SHARED_WORKBENCH_HANDLER_REFS.viewAudit,
  viewAuditBulk: SHARED_WORKBENCH_HANDLER_REFS.viewAuditBulk,
  edit: 'crud:edit',
  copyObjectId: 'clipboard:copy-id',
  viewRelated: 'navigate:related',
  startEntityPermission: 'flow:start:entity.permission',
  startFinanceAccounting: 'flow:start:finance.accounting',
  startInventoryWorkflow: SHARED_WORKBENCH_HANDLER_REFS.startInventoryWorkflow,
  startProductionWorkflow: 'flow:start:v2.material_issue_from_bom',
  startOrderWorkflow: SHARED_WORKBENCH_HANDLER_REFS.startOrder,
  startAssignmentWorkflow: 'flow:start:flow.execute',
  attachDocumentEvidence: 'document:attach_evidence',
} as const;

/** Canonical top-5 action ids — palette · inspector · context menu SSOT per family. */
export const ENTITY_TOP_5_ACTION_IDS = [
  'open_detail',
  'edit',
  'view_audit',
  'start_permission_workflow',
  'copy-object-id',
] as const;

export const FINANCE_TOP_5_ACTION_IDS = [
  'open_detail',
  'edit',
  'view_audit',
  'start_accounting_workflow',
  'view-related-records',
] as const;

export const WAREHOUSE_TOP_5_ACTION_IDS = [
  'open_detail',
  'edit',
  'view_audit',
  'start_inventory_workflow',
  'view-related-records',
] as const;

export const WAREHOUSE_RECEIVING_TOP_5_ACTION_IDS = [
  'open_detail',
  'edit',
  'view_audit',
  'start_supplier_goods_receipt_flow',
  'view-related-records',
] as const;

export const ITEM_TOP_5_ACTION_IDS = [
  'open_detail',
  'edit',
  'view_audit',
  'start_inventory_workflow',
  'view-related-records',
] as const;

export const INVENTORY_TOP_5_ACTION_IDS = [
  'open_detail',
  'edit',
  'view_audit',
  'start_inventory_workflow',
  'view-related-records',
] as const;

export const MANUFACTURING_TOP_5_ACTION_IDS = [
  'open_detail',
  'edit',
  'view_audit',
  'start_production_workflow',
  'view-related-records',
] as const;

export const CUSTOMER_TOP_5_ACTION_IDS = [
  'open_detail',
  'edit',
  'view_audit',
  'start_order_workflow',
  'view-related-records',
] as const;

export const ORDER_TOP_5_ACTION_IDS = [
  'open_detail',
  'edit',
  'view_audit',
  'start_order_workflow',
  'view-related-records',
] as const;

export const SUPPLIER_TOP_5_ACTION_IDS = [
  'open_detail',
  'edit',
  'view_audit',
  'start_inventory_workflow',
  'view-related-records',
] as const;

export const COLLECTION_TOP_5_ACTION_IDS = [
  'open_detail',
  'edit',
  'view_audit',
  'start_collection_contact_flow',
  'view-related-records',
] as const;

export const TASK_TOP_5_ACTION_IDS = [
  'open_detail',
  'edit',
  'view_audit',
  'start_assignment_workflow',
  'view-related-records',
] as const;

/** Space · knowledge · appeal · calendar · event/log reference runtime surfaces. */
export const REFERENCE_TOP_5_ACTION_IDS = [
  'open_detail',
  'edit',
  'view_audit',
  'reference:attach_evidence',
  'view-related-records',
] as const;

export const DOMAIN_BULK_STATE_CHANGE_ACTION_IDS = [
  'bulk_activate',
  'bulk_archive',
  'bulk_delete',
] as const;

export const DOMAIN_BULK_ACTION_IDS = [
  'view_audit_bulk',
  ...DOMAIN_BULK_STATE_CHANGE_ACTION_IDS,
] as const;

const ENTITY_DOMAINS = ['internal_entity', 'external_entity', 'user_entity'] as const;
const FINANCE_DOMAINS = [
  'finance_state',
  'finance_flow',
  'finance_budget',
  'stream_execution',
  'legal_entity',
  'currency_rate',
  'intercompany',
] as const;
const WAREHOUSE_DOMAINS = ['warehouse_picking_list'] as const;
const WAREHOUSE_RECEIVING_DOMAIN = 'warehouse_receiving' as const;
const ITEM_DOMAINS = ['service_item', 'asset_item', 'consumable_item'] as const;
const INVENTORY_DOMAINS = ['inventory_shortage'] as const;
const MANUFACTURING_DOMAINS = [
  'manufacturing_bom',
  'production_order',
  'quality_inspection',
] as const;
/** Thread · inspector alias domains for customer / counterparty relationship surfaces. */
const CUSTOMER_DOMAINS = ['customer', 'counterparty'] as const;
/** Thread · voucher alias domains for sales order workflow surfaces. */
const ORDER_DOMAINS = ['order', 'sales_order'] as const;
/** Thread · inspector alias for supplier relationship surfaces (proxy: entity/external). */
const SUPPLIER_DOMAINS = ['supplier'] as const;
/** Thread · voucher alias for AR collection surfaces (proxy: finance/receivables). */
const COLLECTION_DOMAINS = ['collection', 'ar_collection'] as const;
/** Workspace task hub · flow assignment execution surfaces. */
const TASK_DOMAINS = ['task_execution', 'assignment_execution'] as const;
/** Document grammar aliases — proxied to knowledge/* (Condition ①). */
const DOCUMENT_DOMAINS = [
  'document',
  'document_report',
  'document_policy',
  'document_plan',
  'org_document',
] as const;

/** Knowledge PTG surfaces that share document attach_evidence spawn (OD3-A). */
const KNOWLEDGE_DOCUMENT_SPAWN_DOMAINS = [
  ...DOCUMENT_DOMAINS,
  'knowledge_report',
  'knowledge_policy',
  'knowledge_plan',
] as const;

/** PTG reference runtime without domain-specific workflow starters. */
export const REFERENCE_DOMAINS = [
  'physical_space',
  'virtual_space',
  'knowledge_report',
  'knowledge_policy',
  'knowledge_plan',
  ...DOCUMENT_DOMAINS,
  'semantic_formation',
  'ephemeral_formation',
  'ephemeral_performance',
  'recurrent_performance',
  'calendar_cycle',
  'calendar_trigger',
  'calendar_anchor',
  'flow_definition',
  'stream_definition',
  'action_binding',
  'flow_execution',
  'flow_event',
  'stream_event',
] as const;

const DOMAIN_FAMILY_DOMAINS: Record<DomainActionFamily, readonly string[]> = {
  entity: ENTITY_DOMAINS,
  finance: FINANCE_DOMAINS,
  warehouse: WAREHOUSE_DOMAINS,
  item: ITEM_DOMAINS,
  inventory: INVENTORY_DOMAINS,
  manufacturing: MANUFACTURING_DOMAINS,
  customer: CUSTOMER_DOMAINS,
  supplier: SUPPLIER_DOMAINS,
  order: ORDER_DOMAINS,
  collection: COLLECTION_DOMAINS,
  task: TASK_DOMAINS,
  reference: REFERENCE_DOMAINS,
};

const DOMAIN_TOP_5_BY_FAMILY: Record<DomainActionFamily, readonly string[]> = {
  entity: ENTITY_TOP_5_ACTION_IDS,
  finance: FINANCE_TOP_5_ACTION_IDS,
  warehouse: WAREHOUSE_TOP_5_ACTION_IDS,
  item: ITEM_TOP_5_ACTION_IDS,
  inventory: INVENTORY_TOP_5_ACTION_IDS,
  manufacturing: MANUFACTURING_TOP_5_ACTION_IDS,
  customer: CUSTOMER_TOP_5_ACTION_IDS,
  supplier: SUPPLIER_TOP_5_ACTION_IDS,
  order: ORDER_TOP_5_ACTION_IDS,
  collection: COLLECTION_TOP_5_ACTION_IDS,
  task: TASK_TOP_5_ACTION_IDS,
  reference: REFERENCE_TOP_5_ACTION_IDS,
};

type DomainActionSeed = Omit<WorkbenchAction, 'placement'> & {
  safety: WorkbenchActionSafety;
};

function withSafety(
  seed: Omit<DomainActionSeed, 'safety' | 'riskTier'> & { riskTier: ActionRiskTier },
  overrides?: Partial<WorkbenchActionSafety>
): DomainActionSeed {
  const { riskTier, surfaces, ...rest } = seed;
  const safety = buildActionSafety(riskTier, surfaces, overrides);
  return {
    ...rest,
    surfaces,
    safety,
    riskTier: safety.riskTier,
  };
}

function domainTopActions(
  family: DomainActionFamily,
  seeds: readonly DomainActionSeed[]
): Omit<WorkbenchAction, 'placement'>[] {
  const allowed = new Set(DOMAIN_TOP_5_BY_FAMILY[family]);
  return seeds.filter((seed) => allowed.has(seed.id));
}

function domainTopActionsWithIds(
  allowedIds: readonly string[],
  seeds: readonly DomainActionSeed[]
): Omit<WorkbenchAction, 'placement'>[] {
  const allowed = new Set(allowedIds);
  return seeds.filter((seed) => allowed.has(seed.id));
}

function withDomainBulkActions(
  topActions: readonly Omit<WorkbenchAction, 'placement'>[]
): Omit<WorkbenchAction, 'placement'>[] {
  return [
    ...topActions,
    SHARED_DOMAIN_TOP_ACTIONS.viewAuditBulk,
    SHARED_DOMAIN_TOP_ACTIONS.bulkActivate,
    SHARED_DOMAIN_TOP_ACTIONS.bulkArchive,
    SHARED_DOMAIN_TOP_ACTIONS.bulkDelete,
  ];
}

function buildFamilyRuleEntry(
  family: DomainActionFamily,
  actions: readonly Omit<WorkbenchAction, 'placement'>[]
): ActionRuleEntry {
  return {
    condition: {
      domains: [...DOMAIN_FAMILY_DOMAINS[family]],
      roles: '*',
      statuses: '*',
      planes: ['business'],
    },
    actions: [...actions],
  };
}

const SHARED_DOMAIN_TOP_ACTIONS = {
  openDetail: withSafety({
    id: 'open_detail',
    label: '상세 열기',
    priority: 100,
    surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
    handlerRef: DOMAIN_ACTION_HANDLER_REFS.openDetail,
    group: 'open',
    permissionKey: 'read',
    riskTier: 'low',
  }),
  edit: withSafety({
    id: 'edit',
    label: '수정',
    priority: 90,
    surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
    handlerRef: DOMAIN_ACTION_HANDLER_REFS.edit,
    permissionKey: 'update',
    riskTier: 'medium',
  }),
  viewAudit: withSafety({
    id: 'view_audit',
    label: '감사 기록',
    priority: 80,
    surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
    handlerRef: DOMAIN_ACTION_HANDLER_REFS.viewAudit,
    group: 'admin',
    permissionKey: 'read',
    riskTier: 'low',
  }),
  viewAuditBulk: withSafety({
    id: 'view_audit_bulk',
    label: '감사 기록 (선택)',
    priority: 55,
    surfaces: ['table-row'],
    handlerRef: DOMAIN_ACTION_HANDLER_REFS.viewAuditBulk,
    group: 'state_change',
    permissionKey: 'read',
    riskTier: 'low',
  }),
  bulkActivate: withSafety({
    id: 'bulk_activate',
    label: '활성화 (선택)',
    priority: 52,
    surfaces: ['table-row'],
    handlerRef: BULK_STATE_CHANGE_HANDLER_REFS.activate,
    group: 'state_change',
    permissionKey: 'update',
    riskTier: 'medium',
  }),
  bulkArchive: withSafety({
    id: 'bulk_archive',
    label: '보관 (선택)',
    priority: 51,
    surfaces: ['table-row'],
    handlerRef: BULK_STATE_CHANGE_HANDLER_REFS.archive,
    group: 'state_change',
    permissionKey: 'update',
    riskTier: 'medium',
  }),
  bulkDelete: withSafety({
    id: 'bulk_delete',
    label: '삭제 (선택)',
    priority: 50,
    surfaces: ['table-row'],
    handlerRef: BULK_STATE_CHANGE_HANDLER_REFS.delete,
    group: 'state_change',
    permissionKey: 'delete',
    riskTier: 'high',
  }),
  viewRelated: withSafety({
    id: 'view-related-records',
    label: '관련 보기',
    priority: 42,
    surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
    handlerRef: DOMAIN_ACTION_HANDLER_REFS.viewRelated,
    group: 'attach',
    permissionKey: 'read',
    riskTier: 'low',
  }),
  copyObjectId: withSafety({
    id: 'copy-object-id',
    label: 'ID 복사',
    priority: 45,
    surfaces: ['table-row', 'inspector', 'command'],
    handlerRef: DOMAIN_ACTION_HANDLER_REFS.copyObjectId,
    permissionKey: 'read',
    riskTier: 'low',
  }),
} satisfies Record<string, DomainActionSeed>;

const FLOW_START_ACTION_SEED = (id: string, label: string, handlerRef: string): DomainActionSeed =>
  withSafety({
    id,
    label,
    priority: 86,
    surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
    handlerRef,
    group: 'create',
    permissionKey: 'flow:execute',
    riskTier: 'high',
  });

const ORDER_WORKFLOW_ACTION_SEED = FLOW_START_ACTION_SEED(
  'start_order_workflow',
  '주문 워크플로우',
  DOMAIN_ACTION_HANDLER_REFS.startOrderWorkflow
);

const ENTITY_FAMILY_ACTIONS = withDomainBulkActions(
  domainTopActions('entity', [
    SHARED_DOMAIN_TOP_ACTIONS.openDetail,
    SHARED_DOMAIN_TOP_ACTIONS.edit,
    SHARED_DOMAIN_TOP_ACTIONS.viewAudit,
    withSafety({
      id: 'start_permission_workflow',
      label: '권한 워크플로우',
      priority: 86,
      surfaces: ['table-row', 'inspector', 'command'],
      handlerRef: DOMAIN_ACTION_HANDLER_REFS.startEntityPermission,
      group: 'create',
      permissionKey: 'flow:execute',
      riskTier: 'high',
    }),
    SHARED_DOMAIN_TOP_ACTIONS.copyObjectId,
  ])
);

const FINANCE_FAMILY_ACTIONS = withDomainBulkActions(
  domainTopActions('finance', [
    SHARED_DOMAIN_TOP_ACTIONS.openDetail,
    SHARED_DOMAIN_TOP_ACTIONS.edit,
    SHARED_DOMAIN_TOP_ACTIONS.viewAudit,
    FLOW_START_ACTION_SEED(
      'start_accounting_workflow',
      '회계 워크플로우',
      DOMAIN_ACTION_HANDLER_REFS.startFinanceAccounting
    ),
    SHARED_DOMAIN_TOP_ACTIONS.viewRelated,
  ])
);

const WAREHOUSE_FAMILY_ACTIONS = withDomainBulkActions(
  domainTopActions('warehouse', [
    SHARED_DOMAIN_TOP_ACTIONS.openDetail,
    SHARED_DOMAIN_TOP_ACTIONS.edit,
    SHARED_DOMAIN_TOP_ACTIONS.viewAudit,
    FLOW_START_ACTION_SEED(
      'start_inventory_workflow',
      '재고 워크플로우',
      DOMAIN_ACTION_HANDLER_REFS.startInventoryWorkflow
    ),
    SHARED_DOMAIN_TOP_ACTIONS.viewRelated,
  ])
);

const ITEM_FAMILY_ACTIONS = withDomainBulkActions(
  domainTopActions('item', [
    SHARED_DOMAIN_TOP_ACTIONS.openDetail,
    SHARED_DOMAIN_TOP_ACTIONS.edit,
    SHARED_DOMAIN_TOP_ACTIONS.viewAudit,
    FLOW_START_ACTION_SEED(
      'start_inventory_workflow',
      '재고 워크플로우',
      DOMAIN_ACTION_HANDLER_REFS.startInventoryWorkflow
    ),
    SHARED_DOMAIN_TOP_ACTIONS.viewRelated,
  ])
);

const INVENTORY_FAMILY_ACTIONS = withDomainBulkActions(
  domainTopActions('inventory', [
    SHARED_DOMAIN_TOP_ACTIONS.openDetail,
    SHARED_DOMAIN_TOP_ACTIONS.edit,
    SHARED_DOMAIN_TOP_ACTIONS.viewAudit,
    FLOW_START_ACTION_SEED(
      'start_inventory_workflow',
      '재고 워크플로우',
      DOMAIN_ACTION_HANDLER_REFS.startInventoryWorkflow
    ),
    SHARED_DOMAIN_TOP_ACTIONS.viewRelated,
  ])
);

const MANUFACTURING_FAMILY_ACTIONS = withDomainBulkActions(
  domainTopActions('manufacturing', [
    SHARED_DOMAIN_TOP_ACTIONS.openDetail,
    SHARED_DOMAIN_TOP_ACTIONS.edit,
    SHARED_DOMAIN_TOP_ACTIONS.viewAudit,
    FLOW_START_ACTION_SEED(
      'start_production_workflow',
      '생산 워크플로우',
      DOMAIN_ACTION_HANDLER_REFS.startProductionWorkflow
    ),
    SHARED_DOMAIN_TOP_ACTIONS.viewRelated,
  ])
);

const CUSTOMER_FAMILY_ACTIONS = withDomainBulkActions(
  domainTopActions('customer', [
    SHARED_DOMAIN_TOP_ACTIONS.openDetail,
    SHARED_DOMAIN_TOP_ACTIONS.edit,
    SHARED_DOMAIN_TOP_ACTIONS.viewAudit,
    ORDER_WORKFLOW_ACTION_SEED,
    SHARED_DOMAIN_TOP_ACTIONS.viewRelated,
  ])
);

const ORDER_FAMILY_ACTIONS = withDomainBulkActions(
  domainTopActions('order', [
    SHARED_DOMAIN_TOP_ACTIONS.openDetail,
    SHARED_DOMAIN_TOP_ACTIONS.edit,
    SHARED_DOMAIN_TOP_ACTIONS.viewAudit,
    ORDER_WORKFLOW_ACTION_SEED,
    SHARED_DOMAIN_TOP_ACTIONS.viewRelated,
  ])
);

const SUPPLIER_FAMILY_ACTIONS = withDomainBulkActions(
  domainTopActions('supplier', [
    SHARED_DOMAIN_TOP_ACTIONS.openDetail,
    SHARED_DOMAIN_TOP_ACTIONS.edit,
    SHARED_DOMAIN_TOP_ACTIONS.viewAudit,
    FLOW_START_ACTION_SEED(
      'start_inventory_workflow',
      '재고 워크플로우',
      DOMAIN_ACTION_HANDLER_REFS.startInventoryWorkflow
    ),
    SHARED_DOMAIN_TOP_ACTIONS.viewRelated,
  ])
);

const COLLECTION_CONTACT_FLOW_ACTION_SEED = withSafety({
  id: COLLECTION_DOMAIN_ACTION_IDS.startCollectionContactFlow,
  label: COLLECTION_CONTACT_FLOW_OPERATOR_LABELS.actionStart,
  priority: 87,
  surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
  handlerRef: COLLECTION_DOMAIN_HANDLER_REFS.openContactFlow,
  group: 'create',
  permissionKey: 'flow:execute',
  riskTier: 'medium',
});

const COLLECTION_COMMITMENT_ACTION_SEED = withSafety({
  id: COLLECTION_DOMAIN_ACTION_IDS.recordCollectionCommitment,
  label: COLLECTION_COMMITMENT_OPERATOR_LABELS.actionRecord,
  priority: 86,
  surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
  handlerRef: COLLECTION_DOMAIN_HANDLER_REFS.recordCommitment,
  group: 'create',
  permissionKey: 'flow:execute',
  riskTier: 'medium',
});

const SUPPLIER_GOODS_RECEIPT_FLOW_ACTION_SEED = withSafety({
  id: SUPPLIER_RECEIVING_DOMAIN_ACTION_IDS.startSupplierGoodsReceiptFlow,
  label: SUPPLIER_GOODS_RECEIPT_FLOW_OPERATOR_LABELS.actionStart,
  priority: 87,
  surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
  handlerRef: SUPPLIER_RECEIVING_DOMAIN_HANDLER_REFS.openGoodsReceiptFlow,
  group: 'create',
  permissionKey: 'flow:execute',
  riskTier: 'medium',
});

const WAREHOUSE_RECEIVING_FAMILY_ACTIONS = withDomainBulkActions(
  domainTopActionsWithIds(WAREHOUSE_RECEIVING_TOP_5_ACTION_IDS, [
    SHARED_DOMAIN_TOP_ACTIONS.openDetail,
    SHARED_DOMAIN_TOP_ACTIONS.edit,
    SHARED_DOMAIN_TOP_ACTIONS.viewAudit,
    SUPPLIER_GOODS_RECEIPT_FLOW_ACTION_SEED,
    SHARED_DOMAIN_TOP_ACTIONS.viewRelated,
  ])
);

const COLLECTION_FAMILY_ACTIONS = withDomainBulkActions(
  domainTopActions('collection', [
    SHARED_DOMAIN_TOP_ACTIONS.openDetail,
    SHARED_DOMAIN_TOP_ACTIONS.edit,
    SHARED_DOMAIN_TOP_ACTIONS.viewAudit,
    COLLECTION_CONTACT_FLOW_ACTION_SEED,
    COLLECTION_COMMITMENT_ACTION_SEED,
    SHARED_DOMAIN_TOP_ACTIONS.viewRelated,
  ])
);

const TASK_FAMILY_ACTIONS = withDomainBulkActions(
  domainTopActions('task', [
    SHARED_DOMAIN_TOP_ACTIONS.openDetail,
    SHARED_DOMAIN_TOP_ACTIONS.edit,
    SHARED_DOMAIN_TOP_ACTIONS.viewAudit,
    FLOW_START_ACTION_SEED(
      'start_assignment_workflow',
      '배정 워크플로우',
      DOMAIN_ACTION_HANDLER_REFS.startAssignmentWorkflow
    ),
    SHARED_DOMAIN_TOP_ACTIONS.viewRelated,
  ])
);

const REFERENCE_FAMILY_ACTIONS = withDomainBulkActions(
  domainTopActions('reference', [
    SHARED_DOMAIN_TOP_ACTIONS.openDetail,
    SHARED_DOMAIN_TOP_ACTIONS.edit,
    SHARED_DOMAIN_TOP_ACTIONS.viewAudit,
    SHARED_DOMAIN_TOP_ACTIONS.viewRelated,
    SHARED_DOMAIN_TOP_ACTIONS.copyObjectId,
  ])
);

export const DOMAIN_ACTION_RULE_TABLE: readonly ActionRuleEntry[] = [
  buildFamilyRuleEntry('entity', ENTITY_FAMILY_ACTIONS),
  buildFamilyRuleEntry('finance', FINANCE_FAMILY_ACTIONS),
  buildFamilyRuleEntry('warehouse', WAREHOUSE_FAMILY_ACTIONS),
  buildFamilyRuleEntry('item', ITEM_FAMILY_ACTIONS),
  buildFamilyRuleEntry('inventory', INVENTORY_FAMILY_ACTIONS),
  buildFamilyRuleEntry('manufacturing', MANUFACTURING_FAMILY_ACTIONS),
  buildFamilyRuleEntry('customer', CUSTOMER_FAMILY_ACTIONS),
  buildFamilyRuleEntry('supplier', SUPPLIER_FAMILY_ACTIONS),
  buildFamilyRuleEntry('order', ORDER_FAMILY_ACTIONS),
  buildFamilyRuleEntry('collection', COLLECTION_FAMILY_ACTIONS),
  buildFamilyRuleEntry('task', TASK_FAMILY_ACTIONS),
  buildFamilyRuleEntry('reference', REFERENCE_FAMILY_ACTIONS),
  {
    condition: {
      domains: ['warehouse_receiving'],
      roles: [ROLES.OWNER, ROLES.OPERATIONS_MANAGER],
      statuses: '*',
      planes: ['business'],
    },
    actions: [...WAREHOUSE_RECEIVING_FAMILY_ACTIONS],
  },
  {
    condition: {
      domains: ['finance_state'],
      roles: [ROLES.OWNER, ROLES.OPERATIONS_MANAGER, ROLES.ACCOUNTING],
      statuses: '*',
      planes: ['business'],
    },
    actions: [
      withSafety({
        id: COLLECTION_DOMAIN_ACTION_IDS.startCollectionContactFlow,
        label: COLLECTION_CONTACT_FLOW_OPERATOR_LABELS.actionStart,
        priority: 88,
        surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
        handlerRef: COLLECTION_DOMAIN_HANDLER_REFS.openContactFlow,
        group: 'create',
        permissionKey: 'flow:execute',
        riskTier: 'medium',
      }),
      withSafety({
        id: COLLECTION_DOMAIN_ACTION_IDS.recordCollectionCommitment,
        label: COLLECTION_COMMITMENT_OPERATOR_LABELS.actionRecord,
        priority: 87,
        surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
        handlerRef: COLLECTION_DOMAIN_HANDLER_REFS.recordCommitment,
        group: 'create',
        permissionKey: 'flow:execute',
        riskTier: 'medium',
      }),
    ],
  },
  {
    condition: {
      domains: [...COLLECTION_DOMAINS],
      roles: [ROLES.OWNER, ROLES.OPERATIONS_MANAGER, ROLES.ACCOUNTING],
      statuses: '*',
      planes: ['business'],
    },
    actions: [
      withSafety({
        id: COLLECTION_DOMAIN_ACTION_IDS.startCollectionContactFlow,
        label: COLLECTION_CONTACT_FLOW_OPERATOR_LABELS.actionStart,
        priority: 89,
        surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
        handlerRef: COLLECTION_DOMAIN_HANDLER_REFS.openContactFlow,
        group: 'create',
        permissionKey: 'flow:execute',
        riskTier: 'medium',
      }),
      withSafety({
        id: COLLECTION_DOMAIN_ACTION_IDS.recordCollectionCommitment,
        label: COLLECTION_COMMITMENT_OPERATOR_LABELS.actionRecord,
        priority: 88,
        surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
        handlerRef: COLLECTION_DOMAIN_HANDLER_REFS.recordCommitment,
        group: 'create',
        permissionKey: 'flow:execute',
        riskTier: 'medium',
      }),
      withSafety({
        id: 'collection:open_follow_up_thread',
        label: '수금 후속 Thread',
        priority: 88,
        surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
        handlerRef: 'workbench:intent:collect_payment',
        group: 'create',
        permissionKey: 'flow:execute',
        riskTier: 'medium',
      }),
    ],
  },
  {
    condition: {
      domains: [...KNOWLEDGE_DOCUMENT_SPAWN_DOMAINS],
      roles: [ROLES.OWNER, ROLES.OPERATIONS_MANAGER],
      statuses: '*',
      planes: ['business'],
    },
    actions: [
      withSafety({
        id: 'reference:attach_evidence',
        label: '증빙 첨부',
        priority: 87,
        surfaces: ['table-row', 'detail-header', 'inspector', 'command'],
        handlerRef: DOMAIN_ACTION_HANDLER_REFS.attachDocumentEvidence,
        group: 'attach',
        permissionKey: 'update',
        riskTier: 'medium',
      }),
    ],
  },
  {
    condition: {
      domains: [
        ...ENTITY_DOMAINS,
        ...FINANCE_DOMAINS,
        ...WAREHOUSE_DOMAINS,
        ...ITEM_DOMAINS,
        ...INVENTORY_DOMAINS,
        ...MANUFACTURING_DOMAINS,
        ...CUSTOMER_DOMAINS,
        ...SUPPLIER_DOMAINS,
        ...ORDER_DOMAINS,
        ...COLLECTION_DOMAINS,
        ...TASK_DOMAINS,
        ...REFERENCE_DOMAINS,
      ],
      roles: [ROLES.OWNER, ROLES.OPERATIONS_MANAGER],
      statuses: '*',
      planes: ['business'],
    },
    actions: [
      withSafety({
        id: 'delete',
        label: '삭제',
        priority: 10,
        surfaces: ['table-row', 'detail-header', 'command'],
        handlerRef: 'crud:delete',
        permissionKey: 'delete',
        riskTier: 'high',
      }),
    ],
  },
] as const;

export function resolveDomainActionFamily(businessDomain: string): DomainActionFamily | null {
  const normalized = businessDomain.trim();
  if (ENTITY_DOMAINS.includes(normalized as (typeof ENTITY_DOMAINS)[number])) {
    return 'entity';
  }
  if (FINANCE_DOMAINS.includes(normalized as (typeof FINANCE_DOMAINS)[number])) {
    return 'finance';
  }
  if (WAREHOUSE_DOMAINS.includes(normalized as (typeof WAREHOUSE_DOMAINS)[number])) {
    return 'warehouse';
  }
  if (normalized === WAREHOUSE_RECEIVING_DOMAIN) {
    return 'warehouse';
  }
  if (ITEM_DOMAINS.includes(normalized as (typeof ITEM_DOMAINS)[number])) {
    return 'item';
  }
  if (INVENTORY_DOMAINS.includes(normalized as (typeof INVENTORY_DOMAINS)[number])) {
    return 'inventory';
  }
  if (MANUFACTURING_DOMAINS.includes(normalized as (typeof MANUFACTURING_DOMAINS)[number])) {
    return 'manufacturing';
  }
  if (CUSTOMER_DOMAINS.includes(normalized as (typeof CUSTOMER_DOMAINS)[number])) {
    return 'customer';
  }
  if (SUPPLIER_DOMAINS.includes(normalized as (typeof SUPPLIER_DOMAINS)[number])) {
    return 'supplier';
  }
  if (ORDER_DOMAINS.includes(normalized as (typeof ORDER_DOMAINS)[number])) {
    return 'order';
  }
  if (COLLECTION_DOMAINS.includes(normalized as (typeof COLLECTION_DOMAINS)[number])) {
    return 'collection';
  }
  if (TASK_DOMAINS.includes(normalized as (typeof TASK_DOMAINS)[number])) {
    return 'task';
  }
  if (REFERENCE_DOMAINS.includes(normalized as (typeof REFERENCE_DOMAINS)[number])) {
    return 'reference';
  }
  if (normalized.startsWith('finance')) {
    return 'finance';
  }
  if (normalized.startsWith('warehouse')) {
    return 'warehouse';
  }
  if (normalized.startsWith('inventory')) {
    return 'inventory';
  }
  if (
    normalized.startsWith('manufacturing') ||
    normalized === 'production_order' ||
    normalized === 'quality_inspection'
  ) {
    return 'manufacturing';
  }
  if (normalized.endsWith('_entity')) {
    return 'entity';
  }
  if (normalized.endsWith('_item') && normalized !== 'sellable_item') {
    return 'item';
  }
  if (normalized.startsWith('knowledge_')) {
    return 'reference';
  }
  if (normalized.startsWith('calendar_')) {
    return 'reference';
  }
  if (
    normalized.endsWith('_space') &&
    normalized !== 'workspace_operation' &&
    !normalized.startsWith('warehouse')
  ) {
    return 'reference';
  }
  if (
    normalized.startsWith('ephemeral_') ||
    normalized.startsWith('semantic_') ||
    normalized.startsWith('recurrent_')
  ) {
    return 'reference';
  }
  if (
    normalized.endsWith('_definition') ||
    normalized.endsWith('_execution') ||
    normalized.endsWith('_event') ||
    normalized === 'action_binding'
  ) {
    if (normalized === 'stream_execution') {
      return 'finance';
    }
    if (normalized === 'task_execution' || normalized === 'assignment_execution') {
      return 'task';
    }
    return 'reference';
  }
  return null;
}

export function resolveDomainTop5ActionIds(businessDomain: string): readonly string[] | null {
  const family = resolveDomainActionFamily(businessDomain);
  if (!family) {
    return null;
  }
  return DOMAIN_TOP_5_BY_FAMILY[family];
}

export function collectDomainActionRuleEntries(businessDomain: string): readonly ActionRuleEntry[] {
  const normalized = businessDomain.trim();
  const family = resolveDomainActionFamily(normalized);
  return DOMAIN_ACTION_RULE_TABLE.filter((entry) => {
    const domains = entry.condition.domains;
    if (domains === '*' || domains === undefined) {
      return family !== null;
    }
    if (domains.includes(normalized)) {
      return true;
    }
    if (!family) {
      return false;
    }
    return domains.some((domain) => DOMAIN_FAMILY_DOMAINS[family].includes(domain));
  });
}

/**
 * One anchor domain per grammar family — auto-enrolled in action surface parity gates.
 * Adding a family to DOMAIN_FAMILY_DOMAINS requires updating this map (gate enforces).
 */
export const DOMAIN_ACTION_PARITY_FAMILY_ANCHORS: Readonly<Record<DomainActionFamily, string>> = {
  entity: 'internal_entity',
  finance: 'finance_state',
  warehouse: 'warehouse_picking_list',
  item: 'service_item',
  inventory: 'inventory_shortage',
  manufacturing: 'manufacturing_bom',
  customer: 'customer',
  supplier: 'supplier',
  order: 'order',
  collection: 'collection',
  task: 'task_execution',
  reference: 'knowledge_report',
};

/** Finance depth samples beyond the family anchor (workflow + stream ops). */
export const DOMAIN_ACTION_PARITY_FINANCE_DEPTH_SAMPLES = [
  'finance_flow',
  'finance_budget',
  'stream_execution',
] as const;

/** Manufacturing workflow surface beyond BOM anchor. */
export const DOMAIN_ACTION_PARITY_MANUFACTURING_DEPTH_SAMPLES = [
  'production_order',
  'quality_inspection',
] as const;

/** Assignment inbox beyond task_execution anchor. */
export const DOMAIN_ACTION_PARITY_TASK_DEPTH_SAMPLES = ['assignment_execution'] as const;

/** Knowledge depth samples beyond knowledge_report anchor (document family parity). */
export const DOMAIN_ACTION_PARITY_REFERENCE_DEPTH_SAMPLES = [
  'knowledge_policy',
  'knowledge_plan',
] as const;

/** Sellable golden slice + every family anchor — parity gate auto-enrollment SSOT. */
export function resolveDomainActionParityPilotDomains(): readonly string[] {
  const domains = new Set<string>(['sellable_item']);
  for (const anchor of Object.values(DOMAIN_ACTION_PARITY_FAMILY_ANCHORS)) {
    domains.add(anchor);
  }
  for (const domain of DOMAIN_ACTION_PARITY_FINANCE_DEPTH_SAMPLES) {
    domains.add(domain);
  }
  for (const domain of DOMAIN_ACTION_PARITY_MANUFACTURING_DEPTH_SAMPLES) {
    domains.add(domain);
  }
  for (const domain of DOMAIN_ACTION_PARITY_TASK_DEPTH_SAMPLES) {
    domains.add(domain);
  }
  for (const domain of DOMAIN_ACTION_PARITY_REFERENCE_DEPTH_SAMPLES) {
    domains.add(domain);
  }
  return [...domains].sort();
}

export interface DomainActionParityEnrollmentViolation {
  reason: string;
}

/** Fails when a new domain family is added without parity pilot enrollment. */
export function collectDomainActionParityEnrollmentViolations(): DomainActionParityEnrollmentViolation[] {
  const violations: DomainActionParityEnrollmentViolation[] = [];
  const pilotSet = new Set(resolveDomainActionParityPilotDomains());
  const families = Object.keys(DOMAIN_FAMILY_DOMAINS) as DomainActionFamily[];

  if (families.length !== Object.keys(DOMAIN_ACTION_PARITY_FAMILY_ANCHORS).length) {
    violations.push({
      reason:
        'DOMAIN_ACTION_PARITY_FAMILY_ANCHORS must include an anchor for every DomainActionFamily',
    });
  }

  for (const family of families) {
    const anchor = DOMAIN_ACTION_PARITY_FAMILY_ANCHORS[family];
    if (!anchor) {
      violations.push({ reason: `missing parity anchor for family ${family}` });
      continue;
    }
    if (!DOMAIN_FAMILY_DOMAINS[family].includes(anchor)) {
      violations.push({
        reason: `parity anchor ${anchor} is not listed in DOMAIN_FAMILY_DOMAINS.${family}`,
      });
    }
    if (!pilotSet.has(anchor)) {
      violations.push({
        reason: `family ${family} anchor ${anchor} missing from resolveDomainActionParityPilotDomains()`,
      });
    }
  }

  const seenRuleDomains = new Set<string>();
  for (const entry of DOMAIN_ACTION_RULE_TABLE) {
    const domains = entry.condition.domains;
    if (domains === '*' || domains === undefined) {
      continue;
    }
    for (const domain of domains) {
      if (seenRuleDomains.has(domain) || domain === 'sellable_item') {
        continue;
      }
      seenRuleDomains.add(domain);
      const family = resolveDomainActionFamily(domain);
      if (!family) {
        violations.push({
          reason: `domain rule table domain ${domain} is not mapped by resolveDomainActionFamily`,
        });
      }
    }
  }

  return violations;
}

/** Core action ids superseded by domain manifest (prevents navigate:audit stub parity drift). */
export const DOMAIN_SUPERSEDED_CORE_ACTION_IDS = ['view-audit-trail', 'view'] as const;
